import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { SignOut, Download, GearSix } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const RANGES = ["today", "yesterday", "week", "month"];

export default function ManagerDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [range, setRange] = useState("today");
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/manager", { params: { range } }).then(({ data }) => setD(data)); }, [range]);

  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `aptocore_${kind}.csv`; a.click();
      toast.success(`${kind} exported`);
    } catch { toast.error("Export failed"); }
  };

  const stats = d ? [
    { label: t("started"), value: d.started, tone: "emerald" },
    { label: t("not_started"), value: d.not_started, tone: "red" },
    { label: t("pending_reports"), value: d.pending_reports, tone: "amber" },
    { label: t("doctor_visit"), value: d.doctor_visits },
    { label: t("retailer_visit"), value: d.retailer_visits },
    { label: t("stockist_visit"), value: d.stockist_visits },
    { label: t("order_value"), value: money(d.order_value) },
    { label: t("collection"), value: money(d.collection) },
    { label: t("expenses"), value: money(d.expenses) },
    { label: t("overdue") + " " + t("followups"), value: d.overdue_followups, tone: "red" },
  ] : [];

  const toneCls = { emerald: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };

  return (
    <div className="max-w-5xl mx-auto min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <h1 className="font-head font-bold text-lg text-slate-800">{t("manager_dashboard")}</h1>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <LangToggle />
          {user?.role === "admin" && <button onClick={() => nav("/admin")} data-testid="go-admin" className="p-2"><GearSix size={20} /></button>}
          <button onClick={logout} data-testid="logout-btn" className="p-2 text-red-500"><SignOut size={20} /></button>
        </div>
      </header>

      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)}
                className={`px-3 min-h-[40px] rounded-lg text-sm font-semibold capitalize ${range === r ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{r}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {["visits", "expenses", "attendance"].map((k) => (
              <button key={k} data-testid={`export-${k}`} onClick={() => exportCsv(k)} className="flex items-center gap-1 px-3 min-h-[40px] rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-600 capitalize"><Download size={15} /> {k}</button>
            ))}
          </div>
        </div>

        {d && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-xl bg-white border border-slate-200 p-4">
                  <div className={`font-head font-extrabold text-xl ${toneCls[s.tone] || "text-slate-800"}`}>{s.value}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {d.pending_change_requests > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-center justify-between">
                <span className="text-sm font-semibold text-amber-800">{d.pending_change_requests} {t("approvals")}</span>
                <button onClick={() => nav("/admin")} className="text-sm font-semibold text-amber-700 underline">{t("approvals")}</button>
              </div>
            )}

            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 font-head font-semibold text-slate-800">{t("team_overview")}</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">{t("name")}</th>
                    <th className="px-2">{t("started")}</th>
                    <th className="px-2">{t("visits")}</th>
                    <th className="px-2 text-right pr-4">{t("order_value")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.per_employee.map((e) => (
                    <tr key={e.user_id} className="border-t border-slate-100" data-testid={`emp-row-${e.employee_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{e.name}</div>
                        <div className="text-xs text-slate-400">{e.employee_id}</div>
                      </td>
                      <td className="text-center">
                        <span className={`inline-block h-3 w-3 rounded-full ${e.submitted ? "bg-blue-500" : e.started ? "bg-emerald-500" : "bg-red-400"}`} title={e.submitted ? "submitted" : e.started ? "started" : "not started"} />
                      </td>
                      <td className="text-center font-semibold">{e.visits}/{e.target}</td>
                      <td className="text-right pr-4 font-semibold">{money(e.order_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 font-head font-semibold text-slate-800">{t("recent_visits")}</div>
              <div className="divide-y">
                {(d.recent_visits || []).map((v, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2 text-sm" data-testid="recent-visit-row">
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-800">{v.contact_name}</span>
                      <span className="text-xs text-slate-400 capitalize"> · {v.contact_type} · {v.user_name}</span>
                    </div>
                    <span className="font-semibold text-slate-600 shrink-0">
                      {v.visit_time ? new Date(v.visit_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "--"}
                    </span>
                  </div>
                ))}
                {(!d.recent_visits || d.recent_visits.length === 0) && <p className="px-4 py-6 text-center text-slate-400 text-sm">No visits</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
